import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { TranslateService } from '@ngx-translate/core';

import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { InputDetails, UserPosition } from '@app/inputs/inputs-api.model';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { ProfileService } from '@app/profile/services/profile.service';
import { ProfileCollectionItem } from '@app/profile/profile-api.model';
import { UserOutcomeActionsService } from '@app/profile/components/user-outcome-tile-v2/user-outcome-actions/user-outcome-actions.service';

@Component({
  selector: 'dgx-user-experience-actions',
  templateUrl: './user-experience-actions.component.html',
})
export class UserExperienceActionsComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  // Bindings
  @Input() public userExperience: ProfileCollectionItem<
    // InputDetails - UserExperienceTileComponent TODO: (Should this actually be UserPosition?)
    // UserPosition - ProfileExperienceComponent (v1 & v2), UserExperienceDetailsModalComponent
    UserPosition | InputDetails
  >;
  @Input() public showSelectOption: boolean = false;

  @Output() public itemDeleted = new EventEmitter<UserPosition>();
  @Output() public onModalMenu = new EventEmitter<Event>();

  public userExperienceContent: UserPosition;

  public contentType = 'userPosition';
  public menuConfig: MenuViewModel[];

  public i18n = this.translate.instant([
    'Core_Edit',
    'Core_ReportAProblem',
    'Core_Delete',
    'Core_Select',
    'ProfileCtrl_ExperienceReported',
    'dgProfileOverview_AchievementReported',
    'confirmModal_DeletePositionTitle',
    'confirmModal_DeletePositionBody',
  ]);

  @ViewChild('meatball') private meatball: ElementRef;
  constructor(
    private profileOverviewActionsService: ProfileOverviewActionsService,
    private translate: TranslateService,
    private notifierService: NotifierService,
    private genericModalService: InputModalDispatcherService,
    private profileService: ProfileService,
    private userOutcomeActionsService: UserOutcomeActionsService
  ) {
    super();
  }

  public ngOnInit() {
    this.userExperienceContent = this.userExperience.content as UserPosition;
    this.menuConfig = this.getMenuConfig();
  }

  public getMenuConfig(): MenuViewModel[] {
    const ownerIsViewing = this.profileService.ownerIsViewing;
    return [
      {
        title: this.i18n.Core_Select,
        defaultAction: () =>
          this.userOutcomeActionsService.notifyItemSelected(
            this.userExperience
          ),
        isHidden: () => !ownerIsViewing || !this.showSelectOption,
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_Edit,
        defaultAction: () => this.showEditModal(),
        isHidden: () => !ownerIsViewing,
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_Delete,
        defaultAction: () => this.showDeleteModal(),
        isHidden: () => !ownerIsViewing,
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_ReportAProblem,
        defaultAction: (event) =>
          this.showReportModal(event, this.meatball.nativeElement),
        isHidden: () => false,
        preventRefocus: true,
      },
    ];
  }

  public showEditModal(): void {
    this.onModalMenu.emit();
    const { inputId, userInputId } = this.userExperienceContent;
    this.genericModalService
      .edit({
        inputId,
        userInputId,
        inputType: 'Position',
        initialModel: {
          inputId,
          userInputId,
          inputType: 'Position',
        },
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe((result) => {
        if (!result) {
          return;
        }
      });
  }

  public showDeleteModal(): void {
    this.onModalMenu.emit();
    const payload = {
      contentCategory: 'Input',
      contentType: 'Position',
      itemId: this.userExperienceContent.inputId,
      userItemId: this.userExperienceContent.userInputId,
    };

    // use setTimeout to prevent modal focus being lost
    setTimeout(() =>
      this.userOutcomeActionsService.showDeleteModal(
        this.i18n.confirmModal_DeletePositionTitle,
        this.i18n.confirmModal_DeletePositionBody,
        payload,
        this.userExperienceContent.title,
        this.itemDeleted
      )
    );
  }

  public showReportModal(event, refocusElement: HTMLElement): void {
    this.onModalMenu.emit();
    this.profileOverviewActionsService
      .showReportModal(
        event,
        this.userExperienceContent.userInputId,
        this.contentType,
        refocusElement
      )
      .subscribe(() => {
        this.notifierService.showSuccess(
          this.i18n.ProfileCtrl_ExperienceReported
        );
      });
  }
}
